import { useEffect, useState } from 'react'
import {
  describeWorkerSilence,
  loadProblems,
  loadStuckDocuments,
  workerSilentMinutes,
  type Problem,
  type StuckDocument,
} from '@core/problems'
import { checkSchema, describeSchema } from '@core/schema'
import { Card } from '@design/components'
import { DocumentViewer } from '@features/documents/DocumentViewer'

/**
 * "Werkt alles?"
 *
 * Two questions the admin actually has, on one card: did the app fall over,
 * and is anything stuck. They feel like one question — *what is not working* —
 * so splitting them across two screens would be organising by cause rather
 * than by what someone wants to know.
 *
 * Silent when there is nothing wrong, which is the whole point: this card
 * appearing means something.
 */
export function ProblemsSetting() {
  const [problems, setProblems] = useState<Problem[]>([])
  const [stuck, setStuck] = useState<StuckDocument[]>([])
  const [viewing, setViewing] = useState<string | null>(null)
  const [silence, setSilence] = useState<string | null>(null)
  const [schema, setSchema] = useState<string | null>(null)

  useEffect(() => {
    loadProblems().then(setProblems).catch(() => setProblems([]))
    loadStuckDocuments().then(setStuck).catch(() => setStuck([]))
    workerSilentMinutes()
      .then((m) => setSilence(describeWorkerSilence(m)))
      .catch(() => setSilence(null))
    checkSchema()
      .then((state) => setSchema(describeSchema(state)))
      .catch(() => setSchema(null))
  }, [])

  if (problems.length === 0 && stuck.length === 0 && !silence && !schema) return null

  return (
    <Card className="mb-4 border-warn/40">
      <p className="font-medium mb-1">Er ging iets mis</p>
      <p className="text-sm text-muted mb-3">Van de laatste week.</p>

      {/* First, because it explains the rest. A missing migration is not one
          symptom among several — it is the reason the others are there. */}
      {schema && (
        <p className="text-sm mb-3 p-2 rounded-lg bg-danger/10 text-danger">
          {schema}
        </p>
      )}

      {silence && (
        <p className="text-sm mb-3 p-2 rounded-lg bg-danger/10 text-danger">
          {silence}
        </p>
      )}

      {stuck.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-muted uppercase tracking-wide mb-1">
            Blijven hangen
          </p>
          {stuck.map((doc) => (
            <button
              key={doc.documentId}
              onClick={() => setViewing(doc.documentId)}
              className="block w-full text-left py-1"
            >
              <span className="text-sm">{formatWhen(doc.uploadedAt)}</span>
              <span className="block text-xs text-muted truncate">{doc.reason}</span>
            </button>
          ))}
        </div>
      )}

      {problems.length > 0 && (
        <div>
          <p className="text-xs text-muted uppercase tracking-wide mb-1">In de app</p>
          {problems.map((problem) => (
            <div key={problem.message} className="py-1">
              <p className="text-sm break-words">{problem.message}</p>
              <p className="text-xs text-muted">
                {problem.times}× · laatst {formatWhen(problem.lastSeen)}
              </p>
            </div>
          ))}
        </div>
      )}

      {viewing && (
        <DocumentViewer documentId={viewing} onClose={() => setViewing(null)} />
      )}
    </Card>
  )
}

function formatWhen(iso: string): string {
  return new Intl.DateTimeFormat('nl-BE', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}
